import { useRef, useState } from 'react';
import {
    IconLayoutAlignLeft, IconLayoutAlignCenter, IconLayoutAlignRight,
    IconLayoutAlignTop, IconLayoutAlignMiddle, IconLayoutAlignBottom,
    IconAlignLeft, IconAlignCenter, IconAlignRight, IconAlignJustified,
    IconRotateClockwise, IconAngle, IconDroplet, IconLineHeight,
    IconLetterSpacing, IconDots, IconItalic, IconLink, IconLinkOff, IconPlus,
    IconBorderStyle2, IconLanguage,
} from '@tabler/icons-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
    PanelSection, Row, RowLabel, Field, NumInput, FigSelect, IconSegment, IconBtn, FigCheck,
    figInputCls, T,
} from '../../ui/figma';
import { ColorRow } from '../../ui/ColorControl';
import {
    supportsAspectLock, isAspectLocked, createId, CANVAS_MAX, linkKind, ELEMENT_TYPES,
} from '../../schema/documentSchema';
import { isPathShapeElement } from '../../schema/shapeLibrary';
import {
    getTextPathDef, TEXT_PATH_OPTIONS, TEXT_PATH_ALIGN_OPTIONS,
} from '../../utils/textPathLayout';
import {
    STROKE_POSITION_OPTIONS, getStrokeList, getStrokeWeight, getStrokePosition,
    getEffectList, BORDER_KEYS, isGradientBorder, GRADIENT_ANGLE_DEFAULT,
} from '../propertyControls';
import { FontFamilyPicker, FontStylePicker } from './FontPicker';
import { resolveVariant } from '../../fonts';
import {
    FillControls, CornerRadiusControl, CornerRadiusRow, hasMixedRadii, PaddingControl, EffectRow,
    ShadowSettings, AdjustPopoverButton, BlendModePopoverButton, StrokeEntryRow, EffectEntryRow, ShadowEntrySettings,
    BlurSettings, NoiseSettings, TextureSettings, ShaderSettings, GlassSettings,
} from './controls';

const num = (v) => Number(v) || 0;

/**
 * Which align buttons are already satisfied. Measured against the element's own
 * FRAME (frames sit anywhere on the scene, so the frame origin matters — not 0,0).
 */
function activeCanvasAlignments(el, frame) {
    if (!el || !frame) return new Set();
    const tol = 1.5;
    const active = new Set();
    const { x: fx = 0, y: fy = 0, width: cw, height: ch } = frame;
    if (Math.abs(el.x - fx) <= tol) active.add('left');
    if (Math.abs(el.x - (fx + (cw - el.width) / 2)) <= tol) active.add('center');
    if (Math.abs(el.x + el.width - (fx + cw)) <= tol) active.add('right');
    if (Math.abs(el.y - fy) <= tol) active.add('top');
    if (Math.abs(el.y - (fy + (ch - el.height) / 2)) <= tol) active.add('middle');
    if (Math.abs(el.y + el.height - (fy + ch)) <= tol) active.add('bottom');
    return active;
}

/* ------------------------------------------------------------------ *
 *  Position — align row, X/Y, rotation (admin)
 * ------------------------------------------------------------------ */

export function PositionSection({ el, canvas, setProp, alignSelected, mode }) {
    if (mode !== 'admin') return null;
    const active = activeCanvasAlignments(el, canvas);
    const alignBtn = (value, Icon, label) => (
        <IconBtn
            key={value}
            icon={Icon}
            label={label}
            active={active.has(value)}
            onClick={() => alignSelected(value)}
            className="flex-1"
        />
    );
    // Figma groups the six align buttons as horizontal | vertical, each group
    // filling half the row, and captions every group above its controls.
    return (
        <PanelSection title="Position">
            <Field label="Alignment">
                <Row>
                    <div className={`flex items-center gap-0.5 rounded-[5px] p-0.5 ${T.inputBg}`}>
                        {alignBtn('left', IconLayoutAlignLeft, 'Align left')}
                        {alignBtn('center', IconLayoutAlignCenter, 'Align horizontal centers')}
                        {alignBtn('right', IconLayoutAlignRight, 'Align right')}
                    </div>
                    <div className={`flex items-center gap-0.5 rounded-[5px] p-0.5 ${T.inputBg}`}>
                        {alignBtn('top', IconLayoutAlignTop, 'Align top')}
                        {alignBtn('middle', IconLayoutAlignMiddle, 'Align vertical centers')}
                        {alignBtn('bottom', IconLayoutAlignBottom, 'Align bottom')}
                    </div>
                </Row>
            </Field>
            <Field label="Position">
                <Row>
                    <NumInput label="X" value={Math.round(el.x)} min={-4000} max={8000} onChange={(v) => setProp({ x: v })} />
                    <NumInput label="Y" value={Math.round(el.y)} min={-4000} max={8000} onChange={(v) => setProp({ y: v })} />
                </Row>
            </Field>
            <Field label="Rotation">
                <Row>
                    <NumInput
                        icon={IconAngle}
                        title="Rotation"
                        value={el.rotation || 0}
                        min={-180}
                        max={180}
                        suffix="°"
                        onChange={(v) => setProp({ rotation: v })}
                    />
                    <div className={`flex items-center gap-0.5 rounded-[5px] p-0.5 ${T.inputBg}`}>
                        <IconBtn
                            icon={IconRotateClockwise}
                            label="Rotate 90°"
                            className="flex-1"
                            onClick={() => {
                                let next = (el.rotation || 0) + 90;
                                if (next > 180) next -= 360;
                                setProp({ rotation: next });
                            }}
                        />
                    </div>
                </Row>
            </Field>
        </PanelSection>
    );
}

/* ------------------------------------------------------------------ *
 *  Layout — W/H (admin) + padding (gated)
 * ------------------------------------------------------------------ */

export function LayoutSection({ el, styles, setProp, setStyle, setStyles, mode, gate }) {
    const showSize = mode === 'admin';
    const showPadding = gate('padding');
    if (!showSize && !showPadding) return null;

    // Figma's "constrain proportions" — offered for images, QR codes, barcodes and
    // shapes, whose proportions matter. Stored ON THE ELEMENT rather than as panel
    // state, because the canvas resize handles have to honour it too (see documentSchema).
    const canLock = supportsAspectLock(el);
    const aspectLocked = isAspectLocked(el);

    // Keep the ratio from the CURRENT box, so repeated edits can't drift.
    const setW = (v) => {
        const w = Math.max(1, v);
        if (aspectLocked && el.width > 0) {
            setProp({ width: w, height: Math.max(1, Math.round(w * (el.height / el.width))) });
        } else setProp({ width: w });
    };
    const setH = (v) => {
        const h = Math.max(1, v);
        if (aspectLocked && el.height > 0) {
            setProp({ height: h, width: Math.max(1, Math.round(h * (el.width / el.height))) });
        } else setProp({ height: h });
    };

    return (
        <PanelSection title="Layout">
            {showSize && (
                <Field label="Size">
                    <div className="flex items-center gap-1">
                        <NumInput label="W" value={Math.round(el.width)} min={1} max={8000} onChange={setW} className="flex-1" />
                        <NumInput label="H" value={Math.round(el.height)} min={1} max={8000} onChange={setH} className="flex-1" />
                        {canLock && (
                            <IconBtn
                                icon={aspectLocked ? IconLink : IconLinkOff}
                                label={aspectLocked
                                    ? 'Unlock aspect ratio — free resize from any edge'
                                    : 'Lock aspect ratio — resize from corners only'}
                                active={aspectLocked}
                                onClick={() => setProp({ lockAspect: !aspectLocked })}
                            />
                        )}
                    </div>
                </Field>
            )}
            {showPadding && (
                <PaddingControl styles={styles} setStyle={setStyle} setStyles={setStyles} />
            )}
        </PanelSection>
    );
}

/* ------------------------------------------------------------------ *
 *  Appearance — opacity, corner radius, blend mode, adjust
 * ------------------------------------------------------------------ */

export function AppearanceSection({ el, styles, setProp, setStyle, setStyles, mode, gate, radiusMax = 400 }) {
    const canOpacity = mode === 'admin' || el.editableByUser !== false;
    const showRadius = gate('borderRadius') && !(el.type === 'shape' && (el.shape === 'ellipse' || el.shape === 'line'));
    // A path shape's corners are rounded along its outline, where "top left" has no
    // meaning — one radius for every corner is the only thing that can be honoured.
    const pathShape = isPathShapeElement(el);
    const showBlend = gate('mixBlendMode');
    const showAdjust = mode === 'admin' || el.editableByUser !== false;
    // Owned here, not in the control: the per-corner inputs render below the
    // Opacity/Radius row, where they have the whole section width to sit on.
    const [corners, setCorners] = useState(() => !pathShape && hasMixedRadii(styles));

    return (
        <PanelSection
            title="Appearance"
            actions={(showBlend || showAdjust) ? (
                <>
                    {showBlend && <BlendModePopoverButton styles={styles} setStyle={setStyle} />}
                    {showAdjust && <AdjustPopoverButton styles={styles} setStyle={setStyle} gate={gate} />}
                </>
            ) : null}
        >
            {/* Figma pairs Opacity and Corner radius on one captioned row. */}
            <Row>
                {/* !mb-0: a Field's own bottom margin would stretch this grid row
                    and open a gap above the corner block below it. */}
                <Field label="Opacity" className="!mb-0">
                    <NumInput
                        icon={IconDroplet}
                        title="Opacity"
                        value={Math.round((el.opacity ?? 1) * 100)}
                        min={0}
                        max={100}
                        suffix="%"
                        disabled={!canOpacity}
                        onChange={(v) => setProp({ opacity: Math.min(100, Math.max(0, v)) / 100 })}
                    />
                </Field>
                {showRadius ? (
                    <Field label="Corner radius" className="!mb-0">
                        <CornerRadiusControl
                            styles={styles}
                            setStyle={setStyle}
                            setStyles={setStyles}
                            max={radiusMax}
                            allowIndependent={!pathShape}
                            expanded={corners}
                            onToggleExpanded={setCorners}
                        />
                    </Field>
                ) : <span />}
            </Row>
            {/* Full section width — the same 2×2 block the padding expander uses,
                rather than a squeezed one inside the half-width radius column.
                No top margin of its own: the row above already carries mb-2. */}
            {showRadius && !pathShape && corners && (
                <CornerRadiusRow styles={styles} setStyle={setStyle} max={radiusMax} />
            )}
        </PanelSection>
    );
}

/* ------------------------------------------------------------------ *
 *  Stroke — border / shape stroke
 * ------------------------------------------------------------------ */

const BORDER_STYLE_OPTIONS = [
    { value: 'solid', label: 'Solid' },
    { value: 'dashed', label: 'Dashed' },
    { value: 'dotted', label: 'Dotted' },
    { value: 'double', label: 'Double' },
];

/**
 * Text strokes — a LIST, like Figma: add as many as you want, each with its own
 * colour, opacity, visibility and remove button, all sharing one position and
 * one weight. The stroke follows the GLYPH outline, which is what "stroke" means
 * on a Figma text node; the box outline lives in BorderSection instead.
 *
 * Text only — on every other element type an outline IS the box border, which
 * BorderSection owns.
 */
export function TextStrokeSection({ styles, setStyles, gate }) {
    if (!gate('textStrokeWidth')) return null;
    const strokes = getStrokeList(styles);
    const weight = getStrokeWeight(styles);

    // Writing the list always writes weight + position too, so an element that
    // still carried only the legacy border lands fully on the new model at once.
    const commit = (next, patch = {}) => setStyles({
        strokes: next,
        strokeWeight: weight || 1,
        strokePosition: getStrokePosition(styles),
        // The retired "Text outline" effect wrote these; the list owns the
        // outline now, so clear them or both would paint.
        textStrokeWidth: 0,
        ...patch,
    });
    const patchAt = (i, p) => commit(strokes.map((s, idx) => (idx === i ? { ...s, ...p } : s)));

    // New stroke goes on TOP — index 0 is the topmost paint, as the rows read.
    const addStroke = () => commit([
        { id: createId('stroke'), color: '#000000', visible: true },
        ...strokes,
    ]);

    return (
        <PanelSection
            title="Stroke"
            actions={<IconBtn icon={IconPlus} label="Add stroke" onClick={addStroke} />}
        >
            {strokes.length ? (
                <>
                    {strokes.map((s, i) => (
                        <StrokeEntryRow
                            key={s.id || i}
                            stroke={s}
                            onChange={(p) => patchAt(i, p)}
                            onToggleVisible={() => patchAt(i, { visible: s.visible === false })}
                            onRemove={() => commit(strokes.filter((_, idx) => idx !== i))}
                        />
                    ))}
                    <div className="mt-2">
                        <Row>
                            <div>
                                <RowLabel>Position</RowLabel>
                                <FigSelect
                                    value={getStrokePosition(styles)}
                                    options={STROKE_POSITION_OPTIONS}
                                    onChange={(v) => commit(strokes, { strokePosition: v })}
                                />
                            </div>
                            <div>
                                <RowLabel>Weight</RowLabel>
                                <NumInput
                                    icon={IconBorderStyle2}
                                    title="Stroke weight"
                                    value={weight}
                                    min={0}
                                    max={100}
                                    step={0.5}
                                    onChange={(v) => commit(strokes, { strokeWeight: v })}
                                />
                            </div>
                        </Row>
                    </div>
                </>
            ) : (
                <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                    No strokes. Use + to outline the letters — stack several to layer colours.
                </p>
            )}
        </PanelSection>
    );
}

/**
 * Border — the outline around the element BOX, as opposed to the glyph outline
 * the Stroke section paints. Colour goes through the same fill editor the
 * Background uses, so a border can be a linear or radial gradient.
 *
 * Dash and dot are solid-colour only: a gradient border is drawn as a
 * transparent border with the gradient showing through from the background
 * (the only way to keep border-radius), and that technique has no dashes. The
 * style control hides itself rather than offer something that wouldn't render.
 */
export function BorderSection({
    styles, setStyle, gate,
    // Shapes keep the same concept under stroke* keys — see SHAPE_BORDER_KEYS.
    keys = BORDER_KEYS,
    maxWidth = 100,
    showStyle = true,
    styleOptions = BORDER_STYLE_OPTIONS,
}) {
    if (!gate(keys.width) && !gate(keys.color)) return null;
    const isGradient = isGradientBorder({ ...styles, [keys.width]: 1 }, keys);
    const styleField = showStyle && gate(keys.style);
    return (
        <PanelSection title="Border">
            <Row>
                {/* !mb-0: a Field carries its own bottom margin for stacking, but
                    inside a Row that margin is part of the grid cell and pads the
                    whole row out — the grid's own gap already spaces things. */}
                <Field label="Width" className="!mb-0">
                    <NumInput
                        icon={IconBorderStyle2}
                        title="Border width"
                        value={Number(styles[keys.width]) || 0}
                        min={0}
                        max={maxWidth}
                        step={0.5}
                        onChange={(v) => setStyle(keys.width, v)}
                    />
                </Field>
                {styleField ? (
                    <Field label="Style">
                        <FigSelect
                            value={styles[keys.style] || 'solid'}
                            options={styleOptions}
                            disabled={isGradient}
                            onChange={(v) => setStyle(keys.style, v)}
                        />
                    </Field>
                ) : <span />}
            </Row>
            {/* Unlabelled: the section is already called Border, and a "Color"
                caption over the same Solid/Linear/Radial control the Background
                section shows bare only pushed it away from the row above. */}
            <FillControls
                styles={styles}
                setStyle={setStyle}
                typeKey={keys.fillType}
                solidKey={keys.color}
                solidDefault={keys.colorDefault}
                allowEmptySolid={false}
                gradientPrefix={keys.prefix}
            />
            {isGradient && styleField && (
                <p className={`mt-1.5 text-[10px] leading-snug ${T.textFaint}`}>
                    Gradient borders are always solid — dashes need a single colour.
                </p>
            )}
        </PanelSection>
    );
}

/* ------------------------------------------------------------------ *
 *  Effects — drop / inner shadow (+ text shadow / outline for text)
 * ------------------------------------------------------------------ */

/**
 * Shadows a text element can hold any number of. `singleKey` names the entry in
 * the one-per-element catalogue below that this replaces, so the two lists can't
 * both offer the same effect.
 */
const REPEATABLE_EFFECTS = [
    {
        type: 'dropShadow', label: 'Drop shadow', gateKey: 'shadowBlur', singleKey: 'drop', spread: true,
        defaults: { offsetX: 0, offsetY: 4, blur: 8, spread: 0, color: '#00000040' },
    },
    {
        type: 'innerShadow', label: 'Inner shadow', gateKey: 'innerShadowBlur', singleKey: 'inner', spread: true,
        defaults: { offsetX: 0, offsetY: 2, blur: 4, spread: 0, color: '#00000059' },
    },
    {
        type: 'textShadow', label: 'Text shadow', gateKey: 'textShadowBlur', singleKey: 'textshadow', spread: false,
        defaults: { offsetX: 0, offsetY: 2, blur: 4, color: '#00000073' },
    },
];

/** Zeroed legacy shadow fields — written once the effects LIST takes over. */
const LEGACY_SHADOWS_OFF = {
    shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0, shadowSpread: 0,
    innerShadowBlur: 0, innerShadowOffsetX: 0, innerShadowOffsetY: 0, innerShadowSpread: 0,
    textShadowBlur: 0, textShadowOffsetX: 0, textShadowOffsetY: 0,
};

export function EffectsSection({ el, styles, setStyle, setStyles, gate, isText = false }) {
    // One shared anchor for every effect popover, so each opens in the SAME place
    // instead of stepping down the panel with its own row.
    const anchorRef = useRef(null);
    // Only ONE effect popover open at a time — opening another closes the previous.
    const [openKey, setOpenKey] = useState(null);

    const applyPatch = (patch) => {
        if (setStyles) setStyles(patch);
        else Object.entries(patch).forEach(([k, v]) => setStyle(k, v));
    };

    // Full effect catalogue (matches Figma's "+" add-effect menu). Each entry:
    //   gateKey  — permission key checked via gate()
    //   only     — restrict to text-like elements
    //   isOn     — currently active? (drives the on-dot + whether a row shows)
    //   add      — patch that activates it with sensible defaults
    //   clear    — patch that turns it off
    //   body     — settings popover content
    const catalogue = [
        {
            key: 'drop', label: 'Drop shadow', gateKey: 'shadowBlur',
            isOn: (s) => !!(num(s.shadowBlur) || num(s.shadowOffsetX) || num(s.shadowOffsetY) || num(s.shadowSpread)),
            add: { shadowOffsetX: 0, shadowOffsetY: 4, shadowBlur: 8, shadowSpread: 0, shadowColor: '#00000040' },
            clear: { shadowBlur: 0, shadowOffsetX: 0, shadowOffsetY: 0, shadowSpread: 0 },
            body: () => <ShadowSettings prefix="shadow" styles={styles} setStyle={setStyle} colorKey="shadowColor" colorDefault="#00000040" />,
        },
        {
            key: 'inner', label: 'Inner shadow', gateKey: 'innerShadowBlur',
            isOn: (s) => !!(num(s.innerShadowBlur) || num(s.innerShadowOffsetX) || num(s.innerShadowOffsetY) || num(s.innerShadowSpread)),
            add: { innerShadowOffsetX: 0, innerShadowOffsetY: 2, innerShadowBlur: 4, innerShadowSpread: 0, innerShadowColor: '#00000059' },
            clear: { innerShadowBlur: 0, innerShadowOffsetX: 0, innerShadowOffsetY: 0, innerShadowSpread: 0 },
            body: () => <ShadowSettings prefix="innerShadow" styles={styles} setStyle={setStyle} colorKey="innerShadowColor" colorDefault="#00000059" />,
        },
        {
            key: 'layerBlur', label: 'Layer blur', gateKey: 'layerBlur',
            isOn: (s) => num(s.layerBlur) > 0,
            add: { layerBlur: 4 }, clear: { layerBlur: 0 },
            body: () => <BlurSettings styleKey="layerBlur" value={styles.layerBlur} setStyle={setStyle} max={100} />,
        },
        {
            key: 'bgBlur', label: 'Background blur', gateKey: 'backdropBlur',
            isOn: (s) => num(s.backdropBlur) > 0,
            add: { backdropBlur: 8 }, clear: { backdropBlur: 0 },
            body: () => <BlurSettings styleKey="backdropBlur" value={styles.backdropBlur} setStyle={setStyle} max={60} />,
        },
        {
            key: 'noise', label: 'Noise', gateKey: 'noiseAmount',
            isOn: (s) => num(s.noiseAmount) > 0,
            add: { noiseAmount: 40 }, clear: { noiseAmount: 0 },
            body: () => <NoiseSettings styles={styles} setStyle={setStyle} />,
        },
        {
            key: 'texture', label: 'Texture', gateKey: 'textureType',
            isOn: (s) => !!s.textureType && s.textureType !== 'none' && num(s.textureOpacity) > 0,
            add: { textureType: 'paper', textureOpacity: 40 },
            clear: { textureType: 'none' },
            body: () => <TextureSettings styles={styles} setStyle={setStyle} />,
        },
        {
            key: 'shader', label: 'Shader', gateKey: 'shaderType',
            isOn: (s) => !!s.shaderType && s.shaderType !== 'none' && num(s.shaderOpacity) > 0,
            add: { shaderType: 'mesh', shaderOpacity: 100 },
            clear: { shaderType: 'none' },
            body: () => <ShaderSettings styles={styles} setStyle={setStyle} />,
        },
        {
            key: 'glass', label: 'Glass', gateKey: 'glassBlur',
            isOn: (s) => num(s.glassBlur) > 0,
            add: { glassBlur: 12, glassOpacity: 12, glassHighlight: true }, clear: { glassBlur: 0 },
            body: () => <GlassSettings styles={styles} setStyle={setStyle} />,
        },
        {
            key: 'textshadow', label: 'Text shadow', gateKey: 'textShadowBlur', only: 'text',
            isOn: (s) => !!(num(s.textShadowBlur) || num(s.textShadowOffsetX) || num(s.textShadowOffsetY)),
            add: { textShadowOffsetX: 0, textShadowOffsetY: 2, textShadowBlur: 4, textShadowColor: '#00000073' },
            clear: { textShadowBlur: 0, textShadowOffsetX: 0, textShadowOffsetY: 0 },
            body: () => <ShadowSettings prefix="textShadow" styles={styles} setStyle={setStyle} colorKey="textShadowColor" colorDefault="#00000073" spread={false} />,
        },
    ];

    // Shadows are REPEATABLE for text — Figma lets you stack as many as you like,
    // so they come out of the single-slot catalogue and live in styles.effects.
    // Everything else (blurs, noise, texture, shader, glass, outline) is one-per
    // element in Figma too, and stays a flat style field.
    const repeatable = isText
        ? REPEATABLE_EFFECTS.filter((r) => gate(r.gateKey))
        : [];
    const repeatableKeys = new Set(repeatable.map((r) => r.singleKey));

    const available = catalogue.filter(
        (e) => (!e.only || isText) && gate(e.gateKey) && !repeatableKeys.has(e.key)
    );

    // Once the list exists it is the only source of truth, so the legacy
    // single-value fields are cleared on first write — otherwise a stale
    // shadowBlur would keep painting underneath the list.
    const effectsArray = () => (Array.isArray(styles.effects)
        ? styles.effects
        : REPEATABLE_EFFECTS.flatMap((r) => getEffectList(styles, r.type))
            .map((e) => ({ ...e, id: createId('fx'), visible: true })));

    const commitEffects = (next) => applyPatch({ effects: next, ...LEGACY_SHADOWS_OFF });
    const patchEffect = (id, p) => commitEffects(
        effectsArray().map((e) => (e.id === id ? { ...e, ...p } : e))
    );
    const addEffect = (r) => {
        const id = createId('fx');
        commitEffects([{ id, type: r.type, visible: true, ...r.defaults }, ...effectsArray()]);
        setOpenKey(id);
    };

    const listed = repeatable.flatMap((r) => getEffectList(styles, r.type).map((fx) => ({ r, fx })));
    if (!available.length && !repeatable.length) return null;

    const activeEffects = available.filter((e) => e.isOn(styles));
    const inactiveEffects = available.filter((e) => !e.isOn(styles));

    const addMenu = (inactiveEffects.length || repeatable.length) ? (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <span><IconBtn icon={IconPlus} label="Add effect" /></span>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={6} className="w-48 rounded-[13px] border-[#e6e6e6] dark:border-[#444444] dark:bg-[#2c2c2c] py-1">
                {/* Repeatable first — these can be picked again and again. */}
                {repeatable.map((r) => (
                    <DropdownMenuItem
                        key={r.type}
                        onClick={() => addEffect(r)}
                        className={`text-[11px] px-2.5 py-1.5 mx-1 rounded-[5px] cursor-pointer ${T.text}`}
                    >
                        {r.label}
                    </DropdownMenuItem>
                ))}
                {inactiveEffects.map((e) => (
                    <DropdownMenuItem
                        key={e.key}
                        // Add the effect AND open its settings — no extra click needed.
                        onClick={() => { applyPatch(e.add); setOpenKey(e.key); }}
                        className={`text-[11px] px-2.5 py-1.5 mx-1 rounded-[5px] cursor-pointer ${T.text}`}
                    >
                        {e.label}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    ) : null;

    const anything = listed.length || activeEffects.length;

    return (
        <PanelSection title="Effects" actions={addMenu}>
            <div ref={anchorRef}>
                {listed.map(({ r, fx }) => (
                    <EffectEntryRow
                        key={fx.id}
                        label={r.label}
                        fx={fx}
                        anchorRef={anchorRef}
                        open={openKey === fx.id}
                        onToggleOpen={() => setOpenKey((k) => (k === fx.id ? null : fx.id))}
                        onClose={() => setOpenKey((k) => (k === fx.id ? null : k))}
                        onToggleVisible={() => patchEffect(fx.id, { visible: fx.visible === false })}
                        onRemove={() => {
                            commitEffects(effectsArray().filter((e) => e.id !== fx.id));
                            setOpenKey((k) => (k === fx.id ? null : k));
                        }}
                    >
                        <ShadowEntrySettings
                            fx={fx}
                            spread={r.spread}
                            onChange={(p) => patchEffect(fx.id, p)}
                        />
                    </EffectEntryRow>
                ))}
                {activeEffects.map((e) => (
                    <EffectRow
                        key={e.key}
                        anchorRef={anchorRef}
                        label={e.label}
                        active
                        open={openKey === e.key}
                        onToggle={() => setOpenKey((k) => (k === e.key ? null : e.key))}
                        onClose={() => setOpenKey((k) => (k === e.key ? null : k))}
                        onClear={() => { applyPatch(e.clear); setOpenKey((k) => (k === e.key ? null : k)); }}
                    >
                        {e.body()}
                    </EffectRow>
                ))}
                {!anything && (
                    <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                        No effects. Use + to add a shadow, blur, noise, texture, shader or glass.
                    </p>
                )}
            </div>
        </PanelSection>
    );
}

/* ------------------------------------------------------------------ *
 *  Typography — family, weight/size, spacing, alignment, type settings
 * ------------------------------------------------------------------ */

export function TypographySection({
    styles, setStyle, setStyles, gate,
    // Print artwork is measured in real pixels: a poster imported at 3367×2381 sets
    // its headline at 500pt+, so a 200 cap silently pulled every big heading down
    // the moment the field was touched. The artboard's own limit is the only ceiling
    // that means anything — past it the type is bigger than any page it could print on.
    sizeMax = CANVAS_MAX,
    // Offered only when this layer's text would convert into real Bangla — a
    // Bijoy-era import that predates the automatic conversion, repaired in place.
    onConvertBangla = null,
    // 'width' | 'height' | null. An element field, not a style: whether an edit
    // resizes the box in both axes or only downward. null hides the control (a
    // table cell, a run on a curve — neither has a box of its own to follow).
    autoSize = null,
    onAutoSizeChange = null,
}) {
    const show = ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'textAlign', 'verticalAlign', 'lineHeight', 'letterSpacing'].some(gate);
    if (!show) return null;

    const canSetAutoSize = !!(autoSize && onAutoSizeChange);
    const typeSettings = canSetAutoSize
        || ['textDecoration', 'textTransform', 'whiteSpace', 'fontStyle'].some(gate);
    // Family and style move together (picking a family can force a different
    // cut), so they must land in ONE update or the two writes fight each other
    // in history and the second undo leaves a mismatched pair.
    const patchStyles = setStyles
        || ((patch) => Object.entries(patch).forEach(([k, v]) => setStyle(k, v)));

    return (
        <PanelSection
            title="Typography"
            actions={(
                <>
                    {onConvertBangla && (
                        <IconBtn
                            icon={IconLanguage}
                            label="Convert Bijoy (ANSI) Bangla to Unicode — this text is stored as Latin bytes that only read correctly in SutonnyMJ"
                            onClick={onConvertBangla}
                        />
                    )}
                    {typeSettings ? (
                <Popover>
                    <PopoverTrigger asChild>
                        <span><IconBtn icon={IconDots} label="Type settings" /></span>
                    </PopoverTrigger>
                    <PopoverContent side="left" align="start" sideOffset={12} className="w-[232px] p-3 rounded-[13px] border-[#e6e6e6] dark:border-[#444444] dark:bg-[#2c2c2c] shadow-xl">
                        <div className={`text-[11px] font-semibold mb-2.5 ${T.text}`}>Type settings</div>
                        <div className="flex flex-col gap-2">
                            {gate('fontStyle') && (
                                <div>
                                    <RowLabel>Style</RowLabel>
                                    <IconSegment
                                        value={styles.fontStyle || 'normal'}
                                        onChange={(v) => setStyle('fontStyle', v)}
                                        options={[
                                            { value: 'normal', label: 'Regular', render: () => <span className="text-[11px]">Regular</span> },
                                            { value: 'italic', label: 'Italic', render: () => <IconItalic size={13} /> },
                                        ]}
                                    />
                                </div>
                            )}
                            {gate('textDecoration') && (
                                <div>
                                    <RowLabel>Decoration</RowLabel>
                                    <FigSelect
                                        value={styles.textDecoration || 'none'}
                                        options={[
                                            { value: 'none', label: 'None' },
                                            { value: 'underline', label: 'Underline' },
                                            { value: 'line-through', label: 'Strikethrough' },
                                            { value: 'overline', label: 'Overline' },
                                        ]}
                                        onChange={(v) => setStyle('textDecoration', v)}
                                    />
                                </div>
                            )}
                            {gate('textTransform') && (
                                <div>
                                    <RowLabel>Case</RowLabel>
                                    <FigSelect
                                        value={styles.textTransform || 'none'}
                                        options={[
                                            { value: 'none', label: 'As typed' },
                                            { value: 'uppercase', label: 'Uppercase' },
                                            { value: 'lowercase', label: 'Lowercase' },
                                            { value: 'capitalize', label: 'Title case' },
                                        ]}
                                        onChange={(v) => setStyle('textTransform', v)}
                                    />
                                </div>
                            )}
                            {gate('whiteSpace') && (
                                <div>
                                    <RowLabel>Wrapping</RowLabel>
                                    <FigSelect
                                        value={styles.whiteSpace || 'pre-wrap'}
                                        options={[
                                            { value: 'pre-wrap', label: 'Wrap (pre)' },
                                            { value: 'normal', label: 'Wrap' },
                                            { value: 'nowrap', label: 'No wrap' },
                                            { value: 'pre', label: 'Pre' },
                                        ]}
                                        onChange={(v) => setStyle('whiteSpace', v)}
                                    />
                                </div>
                            )}
                            {canSetAutoSize && (
                                <div>
                                    <RowLabel>Resizing</RowLabel>
                                    <FigSelect
                                        value={autoSize}
                                        options={[
                                            { value: 'width', label: 'Hug text' },
                                            { value: 'height', label: 'Fixed width, grow down' },
                                        ]}
                                        onChange={onAutoSizeChange}
                                    />
                                </div>
                            )}
                        </div>
                    </PopoverContent>
                </Popover>
                    ) : null}
                </>
            )}
        >
            {gate('fontFamily') && (
                <div className="mb-2">
                    <FontFamilyPicker
                        value={styles.fontFamily || 'Hind Siliguri'}
                        weight={Number(styles.fontWeight) || 400}
                        italic={styles.fontStyle === 'italic'}
                        onChange={(family) => {
                            // Snap to a cut the new family actually ships — moving
                            // from a nine-weight family to a Regular-only one must
                            // not leave the element asking for a Bold that would be
                            // faked on canvas and absent from the export.
                            const v = resolveVariant(family, Number(styles.fontWeight) || 400, styles.fontStyle === 'italic');
                            patchStyles({
                                fontFamily: family,
                                fontWeight: v.weight,
                                fontStyle: v.italic ? 'italic' : 'normal',
                            });
                        }}
                    />
                </div>
            )}
            <Row>
                {gate('fontWeight') ? (
                    <FontStylePicker
                        family={styles.fontFamily || 'Hind Siliguri'}
                        weight={Number(styles.fontWeight) || 400}
                        italic={styles.fontStyle === 'italic'}
                        onChange={({ weight, italic }) => patchStyles({
                            fontWeight: weight,
                            fontStyle: italic ? 'italic' : 'normal',
                        })}
                    />
                ) : <span />}
                {gate('fontSize') ? (
                    <NumInput
                        label="Size"
                        title="Font size"
                        value={styles.fontSize || 16}
                        min={4}
                        max={sizeMax}
                        onChange={(v) => setStyle('fontSize', v)}
                    />
                ) : <span />}
            </Row>
            <Row>
                {gate('lineHeight') ? (
                    <NumInput
                        icon={IconLineHeight}
                        title="Line height"
                        value={styles.lineHeight ?? 1.3}
                        min={0.5}
                        max={4}
                        step={0.1}
                        precision={2}
                        onChange={(v) => setStyle('lineHeight', v)}
                    />
                ) : <span />}
                {gate('letterSpacing') ? (
                    <NumInput
                        icon={IconLetterSpacing}
                        title="Letter spacing"
                        value={styles.letterSpacing ?? 0}
                        // Tracking is in pixels, so it has to scale with the type:
                        // ±80 is nothing next to a 500px headline.
                        min={-sizeMax / 2}
                        max={sizeMax}
                        step={0.5}
                        precision={1}
                        onChange={(v) => setStyle('letterSpacing', v)}
                    />
                ) : <span />}
            </Row>
            {(gate('textAlign') || gate('verticalAlign')) && (
                <Row>
                    {gate('textAlign') ? (
                        <IconSegment
                            value={styles.textAlign || 'left'}
                            onChange={(v) => setStyle('textAlign', v)}
                            options={[
                                { value: 'left', label: 'Align left', icon: IconAlignLeft },
                                { value: 'center', label: 'Align center', icon: IconAlignCenter },
                                { value: 'right', label: 'Align right', icon: IconAlignRight },
                                { value: 'justify', label: 'Justify', icon: IconAlignJustified },
                            ]}
                        />
                    ) : <span />}
                    {gate('verticalAlign') ? (
                        <IconSegment
                            value={styles.verticalAlign || 'top'}
                            onChange={(v) => setStyle('verticalAlign', v)}
                            options={[
                                { value: 'top', label: 'Align top', icon: IconLayoutAlignTop },
                                { value: 'middle', label: 'Align middle', icon: IconLayoutAlignMiddle },
                                { value: 'bottom', label: 'Align bottom', icon: IconLayoutAlignBottom },
                            ]}
                        />
                    ) : <span />}
                </Row>
            )}
        </PanelSection>
    );
}

/* ------------------------------------------------------------------ *
 *  Content — text / placeholder editing
 * ------------------------------------------------------------------ */

/**
 * Placeholder wiring — the key and whether the end user may edit it.
 *
 * No text box here: double-clicking the element on canvas edits it in place,
 * which is where you can see what you are typing. Plain text elements have
 * nothing else to configure, so the section hides itself entirely for them.
 */
export function ContentSection({ el, setProp, mode }) {
    if (el.type !== 'placeholder' || mode !== 'admin') return null;
    return (
        <PanelSection title="Placeholder">
            <RowLabel>Placeholder key</RowLabel>
            <input
                className={`${figInputCls} mb-2 font-mono`}
                value={el.placeholderKey || ''}
                onChange={(e) => {
                    const key = e.target.value.replace(/\s+/g, '_');
                    setProp({ placeholderKey: key, content: `{{${key}}}` });
                }}
            />
            <FigCheck
                label="User editable"
                checked={el.editableByUser !== false}
                onChange={(v) => setProp({ editableByUser: v })}
            />
        </PanelSection>
    );
}

/**
 * Linked text and photos — the admin half of "fill it in once, it lands
 * everywhere".
 *
 * A card names the bride three times in three different sizes; an ID sheet shows
 * the same face at passport size and again as a stamp. Linking those layers means
 * the person filling it in sees ONE field: the content is shared, the design is
 * not, so each place keeps its own font, colour, box and crop.
 *
 * Built as a section on the layer rather than a separate mode because that is
 * where an admin is already standing when they notice the repetition: select the
 * places, name the field, done.
 */
export function LinkedContentSection({
    el, selectedIds = [], elements = [], mode,
    onLink, onUnlink, onRename,
}) {
    const [label, setLabel] = useState('');
    const kind = linkKind(el);
    if (mode !== 'admin' || !kind) return null;

    // A photo cannot follow a text field, so only the layers that share this
    // one's kind count towards a link.
    const members = el.contentKey
        ? elements.filter((e) => e.contentKey === el.contentKey)
        : [];
    const selectedPeers = selectedIds.filter((id) => {
        const t = elements.find((e) => e.id === id);
        return linkKind(t) === kind;
    });
    // A QR code and a barcode link AS text — they carry a value the way a heading
    // carries words — but calling their section "Linked text" in front of an admin
    // staring at a QR code reads as the wrong section, so the words follow the
    // layer while the kind stays what it is.
    const isCode = el.type === ELEMENT_TYPES.QRCODE || el.type === ELEMENT_TYPES.BARCODE;
    const title = kind === 'image' ? 'Linked photo'
        : kind === 'table' ? 'Linked table'
            : isCode ? 'Linked value' : 'Linked text';
    const noun = kind === 'image' ? 'photo' : kind === 'table' ? 'table' : isCode ? 'code' : 'text';
    const sameThing = kind === 'image' ? 'show the same photo'
        : kind === 'table' ? 'show the same table'
            : isCode ? 'encode the same value' : 'say the same thing';
    const kept = kind === 'image'
        ? 'Sizes, crops and borders stay as they are.'
        : kind === 'table'
            ? 'Fonts, colours and borders stay as they are.'
            : isCode
                ? 'Colours, sizes and the code style stay as they are.'
                : 'Colours, sizes and positions stay as they are.';
    const fills = kind === 'image' ? 'uploads a photo'
        : kind === 'table' ? 'edits the cells' : 'types';

    return (
        <PanelSection title={title}>
            {members.length ? (
                <>
                    <RowLabel>Field name — what the user sees above the box</RowLabel>
                    <input
                        className={`${figInputCls} mb-2`}
                        value={el.contentLabel || ''}
                        placeholder="Field name"
                        onChange={(e) => onRename?.(el.contentKey, e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                    <div className="flex items-center justify-between gap-2">
                        <span className={`text-[11px] ${T.textSoft}`}>
                            Shared with {members.length - 1} other place{members.length === 2 ? '' : 's'}
                            {el.contentDetached ? ' — this one writes its own' : ''}
                        </span>
                        <button
                            type="button"
                            title="Break the link for every place in this group"
                            onClick={() => onUnlink?.(members.map((m) => m.id))}
                            className={`h-6 px-2 shrink-0 flex items-center gap-1 rounded-[5px] text-[11px] ${T.hoverBg} ${T.textSoft}`}
                        >
                            <IconLinkOff size={13} stroke={1.75} /> Unlink
                        </button>
                    </div>
                </>
            ) : selectedPeers.length > 1 ? (
                <>
                    <RowLabel>Field name — what the user sees above the box</RowLabel>
                    <input
                        className={`${figInputCls} mb-2`}
                        value={label}
                        placeholder="Field name"
                        onChange={(e) => setLabel(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                    <button
                        type="button"
                        onClick={() => { onLink?.(selectedPeers, label); setLabel(''); }}
                        className={`w-full h-7 flex items-center justify-center gap-1.5 rounded-[5px]
                            text-[11px] font-medium bg-[#0d99ff] text-white hover:bg-[#0b87e3] transition-colors`}
                    >
                        <IconLink size={13} stroke={1.75} />
                        Link {selectedPeers.length} {noun} layers as one field
                    </button>
                    <p className={`mt-1.5 text-[10px] leading-snug ${T.textFaint}`}>
                        Nothing changes on the artboard now — each place keeps what you
                        designed it with. From the moment the user {fills}, they all
                        follow. {kept}
                    </p>
                </>
            ) : (
                <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                    Select this and the other places that should {sameThing}
                    (⇧-click on canvas), then link them into one field for the user.
                </p>
            )}
        </PanelSection>
    );
}

/* ------------------------------------------------------------------ *
 *  Generic fill section wrapper
 * ------------------------------------------------------------------ */

export function FillSection({ title = 'Fill', ...props }) {
    const { gate = () => true, typeKey = 'backgroundType', solidKey = 'backgroundColor' } = props;
    if (![typeKey, solidKey].some(gate)) return null;
    return (
        <PanelSection title={title}>
            <FillControls {...props} />
        </PanelSection>
    );
}

/**
 * A text element's glyph fill.
 *
 * Two things share this section because they are the same question — "what colour
 * are these letters?" — asked at two scopes:
 *
 *  - nothing selected inside the text: the fill of the WHOLE element, solid or
 *    gradient, through the same editor every other fill uses;
 *  - characters selected while editing on canvas: just those characters, which is
 *    how one heading ends up two colours without being split into two elements.
 *
 * Both scopes get the SAME editor — solid, linear or radial. A run's gradient is
 * painted across the run's own box (per line where it wraps), which is what CSS
 * `box-decoration-break: clone` gives the editor and what the export draws.
 */
export function TextFillSection({
    styles, setStyle, gate,
    range = null, onRangeFill, rangePaint = null,
    hasSpans = false, onClearSpans,
}) {
    if (!gate('color') && !gate('textFillType')) return null;

    // The selected run's fill, in the same style keys FillControls edits for the
    // element — so one control serves both scopes. Absent values fall back to the
    // element's own fill, which is what the characters are painted in right now.
    const rangeStyles = range ? {
        textFillType: rangePaint?.fill?.type || 'solid',
        color: rangePaint?.color || styles.color || '#18181b',
        textGradientColor1: rangePaint?.fill?.color1 ?? styles.textGradientColor1,
        textGradientColor2: rangePaint?.fill?.color2 ?? styles.textGradientColor2,
        textGradientAngle: rangePaint?.fill?.angle ?? styles.textGradientAngle,
        textGradientCenterX: rangePaint?.fill?.centerX ?? styles.textGradientCenterX,
        textGradientCenterY: rangePaint?.fill?.centerY ?? styles.textGradientCenterY,
    } : null;

    /** Edit one or more fields of the run's fill and write the whole paint back. */
    const setRangeStyles = (patch) => {
        const next = { ...rangeStyles, ...patch };
        const type = next.textFillType;
        if (type !== 'linear' && type !== 'radial') {
            onRangeFill?.({ color: next.color });
            return;
        }
        onRangeFill?.({
            color: next.color,
            fill: {
                type,
                // A gradient that has never been set starts from the colour the run
                // already shows, so switching to it is a visible nudge, not a jump.
                color1: next.textGradientColor1 || next.color,
                color2: next.textGradientColor2 || '#ffffff',
                angle: next.textGradientAngle ?? GRADIENT_ANGLE_DEFAULT,
                centerX: next.textGradientCenterX ?? 50,
                centerY: next.textGradientCenterY ?? 50,
            },
        });
    };
    const setRangeStyle = (key, value) => setRangeStyles({ [key]: value });

    return (
        <PanelSection title="Fill">
            {range ? (
                <>
                    <RowLabel>Selected text</RowLabel>
                    <FillControls
                        styles={rangeStyles}
                        setStyle={setRangeStyle}
                        setStyles={setRangeStyles}
                        gate={gate}
                        typeKey="textFillType"
                        solidKey="color"
                        solidDefault="#18181b"
                        allowEmptySolid={false}
                        gradientPrefix="text"
                    />
                    <button
                        type="button"
                        onClick={() => onRangeFill?.(null)}
                        className={`mt-2 w-full h-7 flex items-center justify-center rounded-[5px] border ${T.border}
                            text-[11px] font-medium ${T.text} ${T.hoverBg}`}
                    >
                        Reset to the element fill
                    </button>
                    <p className={`mt-1.5 text-[10px] leading-snug ${T.textFaint}`}>
                        {range.end - range.start} character{range.end - range.start > 1 ? 's' : ''} selected.
                        Click the element again to go back to colouring all of it.
                    </p>
                </>
            ) : (
                <>
                    <FillControls
                        styles={styles}
                        setStyle={setStyle}
                        gate={gate}
                        typeKey="textFillType"
                        solidKey="color"
                        solidDefault="#18181b"
                        allowEmptySolid={false}
                        gradientPrefix="text"
                    />
                    {hasSpans && (
                        <>
                            <button
                                type="button"
                                onClick={() => onClearSpans?.()}
                                className={`mt-2 w-full h-7 flex items-center justify-center rounded-[5px] border ${T.border}
                                    text-[11px] font-medium ${T.text} ${T.hoverBg}`}
                            >
                                Clear per-character colours
                            </button>
                            <p className={`mt-1.5 text-[10px] leading-snug ${T.textFaint}`}>
                                Some characters have their own colour and ignore this fill.
                                Double-click the text and select them to change it.
                            </p>
                        </>
                    )}
                </>
            )}
        </PanelSection>
    );
}

/** Single solid color row section (text fill, table text color, …) */
export function SolidColorSection({ title, value, onChange, allowEmpty = false }) {
    return (
        <PanelSection title={title}>
            <ColorRow label={title} value={value} allowEmpty={allowEmpty} onChange={onChange} />
        </PanelSection>
    );
}

/* ------------------------------------------------------------------ *
 *  Path — bind a text block to a curve (Figma "Text on path")
 * ------------------------------------------------------------------ */

/**
 * The curve is inscribed in the element's own box, so there is no separate size
 * control here: resizing the text box reshapes the path (a wide box gives a flat
 * ellipse, a square one a circle), which is how the guide on canvas behaves.
 */
export function TextPathSection({ el, setProp, mode }) {
    const locked = mode === 'user' && el.editableByUser === false;
    const current = getTextPathDef(el.textPath)?.key || 'none';
    const on = current !== 'none';

    const choose = (value) => {
        if (value === 'none') {
            setProp({ textPath: null });
            return;
        }
        // Start is per-path (the crown of an arch is nowhere near the top of a
        // circle), so re-binding re-seeds it; align and side carry over.
        setProp({
            textPath: value,
            pathStart: getTextPathDef(value).start || 0,
            pathAlign: el.pathAlign || 'center',
            pathSide: el.pathSide || 'outside',
        });
    };

    return (
        <PanelSection title="Path">
            <Field label="Shape">
                <FigSelect
                    value={current}
                    options={TEXT_PATH_OPTIONS}
                    disabled={locked}
                    onChange={choose}
                />
            </Field>
            {on && (
                <>
                    <Row>
                        <Field label="Start">
                            <NumInput
                                label="%"
                                title="Where the run begins, as a percentage along the path"
                                value={Number(el.pathStart) || 0}
                                min={-100}
                                max={200}
                                disabled={locked}
                                onChange={(v) => setProp({ pathStart: v })}
                            />
                        </Field>
                        <Field label="Align">
                            <FigSelect
                                value={el.pathAlign || 'center'}
                                options={TEXT_PATH_ALIGN_OPTIONS}
                                disabled={locked}
                                onChange={(v) => setProp({ pathAlign: v })}
                            />
                        </Field>
                    </Row>
                    <Field label="Side">
                        <IconSegment
                            value={el.pathSide || 'outside'}
                            onChange={(v) => setProp({ pathSide: v })}
                            options={[
                                { value: 'outside', label: 'Outside the curve', render: () => <span className="text-[11px]">Outside</span> },
                                { value: 'inside', label: 'Flipped to the inside', render: () => <span className="text-[11px]">Inside</span> },
                            ]}
                        />
                    </Field>
                    <p className={`text-[10px] leading-snug ${T.textFaint}`}>
                        Resize the box to reshape the curve. Letters may overhang it — that is
                        the frame, not a clip.
                    </p>
                </>
            )}
        </PanelSection>
    );
}
